/**
 * Native-shell bridge (Capacitor) — LEGACY, retained but retired.
 *
 * The Capacitor WebView shell (formerly /mobile) was retired on 2026-08-21;
 * the native SwiftUI app at /ios is the iOS client (see
 * docs/internal/web-ios-parity-plan.md). This module stays because it is
 * inert in browsers — every accessor checks for `window.Capacitor`, which
 * only a Capacitor WebView injects — and removing it would churn the auth
 * code paths for no runtime gain. Do not add new native features here.
 *
 * Auth model (Sprint 1, historical): on native the app used the bearer-token
 * flow — the OAuth callback forwarded a single-use code to
 * `stagster://auth?code=…`, the app exchanged it at
 * POST /api/auth/native-token, and the JWT was stored in Capacitor
 * Preferences. The web cookie flow stays untouched.
 */
const PREFERENCES_KEY = "stagster_auth_token";

export const isNative = () =>
  typeof window !== "undefined" && Boolean(window.Capacitor?.getPlatform?.());

export const getNativePlatform = () => {
  try {
    return window.Capacitor?.getPlatform?.() || null;
  } catch {
    return null;
  }
};

export const isNativeIOS = () => getNativePlatform() === "ios";
export const isNativeAndroid = () => getNativePlatform() === "android";

const prefs = () => window.Capacitor?.Plugins?.Preferences;

/** Read the stored native session JWT (or null on web / when absent). */
export async function getNativeToken() {
  if (!isNative()) return null;
  try {
    const { value } = (await prefs()?.get({ key: PREFERENCES_KEY })) || {};
    return typeof value === "string" && value ? value : null;
  } catch (err) {
    console.warn("[native] getNativeToken failed", err);
    return null;
  }
}

/** Store the native session JWT. Returns true on success. */
export async function setNativeToken(token) {
  if (!isNative() || !token) return false;
  try {
    await prefs()?.set({ key: PREFERENCES_KEY, value: token });
    return true;
  } catch (err) {
    console.warn("[native] setNativeToken failed", err);
    return false;
  }
}

/** Remove the native session JWT (logout / 401). */
export async function clearNativeToken() {
  if (!isNative()) return;
  try {
    await prefs()?.remove({ key: PREFERENCES_KEY });
  } catch (err) {
    console.warn("[native] clearNativeToken failed", err);
  }
}

/**
 * URL that starts Google OAuth. On native it adds `platform=native` (and the
 * `stagster` scheme), so the web callback forwards the single-use code to the
 * app instead of setting a cookie. Same-origin so it works inside the WebView.
 */
export function getNativeAuthStartUrl(next = "/") {
  const url = new URL("/api/auth/google/start", window.location.origin);
  url.searchParams.set("next", next);
  if (isNative()) {
    url.searchParams.set("platform", "native");
    url.searchParams.set("scheme", "stagster");
  }
  return url.toString();
}
